import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'
import { Button, ButtonLink, Notice } from '@/components/play'
import { safeReturnTo } from '@/components/onboarding/authContinuation'

type VerifyResponse = { data: { token: string; return_to?: string | null } }
type VerifyError = { response?: { status?: number; data?: { error?: { message?: string } } } }
/** `ready` can still be tapped, with an error from a try that may work again; `failed` is final. */
type Status = { kind: 'ready' | 'signing-in' | 'failed'; error?: string }

const INCOMPLETE_LINK = 'This sign-in link is incomplete. Ask for a new one.'
const UNUSABLE_LINK = "This sign-in link can't be used. Ask for a new one."
const TRY_AGAIN = "We couldn't sign you in. Try again."

/** Finishes a sign-in from the emailed link, /auth/magic-link#token=…&return_to=…. */
export function MagicLinkPage() {
  const navigate = useNavigate()
  const { completeOAuthLogin } = useAuth()
  // Read once; the effect below then removes them from the address bar and history.
  const [link] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    return { token: params.get('token'), returnTo: safeReturnTo(params.get('return_to'), '/') }
  })
  const [status, setStatus] = useState<Status>(link.token ? { kind: 'ready' } : { kind: 'failed', error: INCOMPLETE_LINK })
  const posting = useRef(false)

  useEffect(() => {
    window.history.replaceState(window.history.state, '', window.location.pathname)
  }, [])

  // Only a tap posts the token, so a forwarded link or a mail scanner that
  // opens the page can't sign anyone in.
  function signIn() {
    if (!link.token || posting.current) return
    posting.current = true
    setStatus({ kind: 'signing-in' })
    api.post<VerifyResponse>('/auth/magic_link/verify', { token: link.token })
      .then((res) => {
        completeOAuthLogin(res.data.token)
        navigate(safeReturnTo(res.data.return_to), { replace: true })
      })
      .catch((err: VerifyError) => {
        posting.current = false
        const code = err?.response?.status
        const message = err?.response?.data?.error?.message
        // 401 and 403 mean the link is used up or the account can't use one;
        // anything else (a throttle, a network error) leaves the link working.
        if (code === 401 || code === 403) setStatus({ kind: 'failed', error: message || UNUSABLE_LINK })
        else setStatus({ kind: 'ready', error: message || TRY_AGAIN })
      })
  }

  return (
    <PublicAuthLayout eyebrow="Sign in" title="Nearly there." description="Tap to finish signing in." backLink={{ to: '/', label: 'StreamBridge' }}>
      {status.kind === 'failed' ? (
        <div className="space-y-5">
          <Notice tone="error" alert>{status.error}</Notice>
          <ButtonLink to={link.returnTo} className="w-full">Continue without signing in</ButtonLink>
        </div>
      ) : (
        <div className="space-y-5">
          {status.error && <Notice tone="error" alert>{status.error}</Notice>}
          <Button className="w-full" onClick={signIn} loading={status.kind === 'signing-in'}>Sign in</Button>
        </div>
      )}
    </PublicAuthLayout>
  )
}
